import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCustomerHealthPortfolioPage, fetchCustomerHealthSummary } from '../../services/customerHealthService';
import { useUsersLookup } from '../../hooks/useUsersLookup';
import { useRoles } from '../../hooks/useRoles';
import useAuthStore from '../../stores/authStore';
import useCustomerHealthStore from '../../stores/customerHealthStore';
import { PortfolioRow as PortfolioRowType, PortfolioSummary } from './types';
import { canManageChPortfolio } from './helpers';
import { SummaryBand, PortfolioRow, PortfolioPagination } from './components/portfolio';
import { AddParentModal } from './components/AddParentModal';

/**
 * Customer Health — Portfolio (index route `/dashboard/customer-health`).
 * A server-paginated, name-sorted list of tracked ultimate parents (from the
 * `customer_health_portfolio` view) + a whole-portfolio KPI band. Rows link to each
 * parent's detail page. Owns the "Add Ultimate Parent" flow.
 */
function Portfolio() {
  const navigate = useNavigate();
  const { byId } = useUsersLookup();
  const role = useAuthStore((s) => s.user?.role);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { roles } = useRoles();
  const currentUserHierarchy = roles.find((r) => r.name === role)?.role_hierarchy;
  const canManagePortfolio = canManageChPortfolio(currentUserHierarchy);

  const offset = useCustomerHealthStore((s) => s.offset);
  const pageSize = useCustomerHealthStore((s) => s.pageSize);
  const sortBy = useCustomerHealthStore((s) => s.sortBy);
  const setOffset = useCustomerHealthStore((s) => s.setOffset);
  const setPageSize = useCustomerHealthStore((s) => s.setPageSize);
  const setSortBy = useCustomerHealthStore((s) => s.setSortBy);

  const [rows, setRows] = useState<PortfolioRowType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Paginated list — refetch whenever pagination/sort changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCustomerHealthPortfolioPage({ limit: pageSize, offset, sortBy })
      .then((res) => { if (!cancelled) { setRows(res.rows); setTotalCount(res.totalCount); } })
      .catch(() => { if (!cancelled) setError('Failed to load the customer health portfolio.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [offset, pageSize, sortBy]);

  // Whole-portfolio KPI band — fetched once per mount (independent of paging).
  useEffect(() => {
    let cancelled = false;
    fetchCustomerHealthSummary()
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { /* KPI band just stays hidden on failure */ });
    return () => { cancelled = true; };
  }, []);

  const excludeParents = useMemo(() => rows.map((r) => r.ultimateParent), [rows]);
  const openParent = (name: string) => { navigate('/dashboard/customer-health/' + encodeURIComponent(name)); window.scrollTo(0, 0); };

  return (
    <main className="ch-main ch-fade">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>Customer Health</h1>
        <p style={{ margin: '6px 0 0', fontSize: 14, color: '#666' }}>
          Portfolio oversight across {summary ? summary.trackedCount : totalCount} ultimate parents. Select a parent to open its full health record.
        </p>
      </div>

      {error && (
        <div style={{ fontSize: 13, color: '#b01400', background: 'rgba(225,25,0,.08)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>{error}</div>
      )}

      {summary && <SummaryBand summary={summary} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, marginTop: summary ? 0 : 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '.5px' }}>Ultimate Parents</div>
        {canManagePortfolio && (
          <button className="ch-btn-primary" onClick={() => setShowAdd(true)}>+ Add Ultimate Parent</button>
        )}
      </div>

      <PortfolioPagination
        offset={offset}
        pageSize={pageSize}
        totalCount={totalCount}
        sortBy={sortBy}
        onSort={setSortBy}
        onPageSize={setPageSize}
        onOffset={setOffset}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div className="ch-card" style={{ padding: 32, textAlign: 'center', fontSize: 14, color: '#999' }}>Loading portfolio…</div>
        ) : rows.length ? (
          rows.map((row) => <PortfolioRow key={row.ultimateParent} row={row} byId={byId} onOpen={() => openParent(row.ultimateParent)} />)
        ) : (
          <div className="ch-card" style={{ padding: 28, textAlign: 'center', fontSize: 14, color: '#999' }}>No ultimate parents tracked yet.</div>
        )}
      </div>

      {showAdd && (
        <AddParentModal
          excludeParents={excludeParents}
          currentUserId={currentUserId}
          onClose={() => setShowAdd(false)}
          onAdded={(name) => { setShowAdd(false); openParent(name); }}
        />
      )}
    </main>
  );
}

export default Portfolio;
